import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Badge,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Settings,
  Logout,
  Person,
  Checklist,
  NewReleases,
  Explore,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePermission } from '../../hooks/usePermission';
import { useOnboarding } from '../onboarding/OnboardingContext';
import { TOUR } from '../onboarding/tourTargets';
import { WarehouseAPI } from '../../services/api';

interface HeaderProps {
  title?: string;
}

const Header: React.FC<HeaderProps> = ({ title = 'Warehouse Bundle Management' }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { openReleaseHistory, startTour } = useOnboarding();
  // Inventory sync (Sync Now button + syncing status chip) tracks the sync-inventory EDIT
  // capability (matches the backend requirePermission on the sync routes).
  const canSeeInventorySync = usePermission('sync-inventory').can('edit');
  // Settings is gated on the settings capability so the menu item is correct for every role
  // (shown only to users who can view settings), mirroring the /settings PermissionRoute.
  const canViewSettings = usePermission('settings').can('view');
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [openActionables, setOpenActionables] = useState(0);
  const [actionablesScope, setActionablesScope] = useState<'all' | 'mine'>('mine');
  const [ucSyncing, setUcSyncing] = useState(false);

  useEffect(() => {
    if (!user) return;
    const refresh = () => WarehouseAPI.getMyActionableCount().then(c => { setOpenActionables(c.open); setActionablesScope(c.scope); }).catch(() => undefined);
    refresh();
    const t = setInterval(refresh, 5 * 60 * 1000);
    window.addEventListener('actionables-changed', refresh);
    return () => { clearInterval(t); window.removeEventListener('actionables-changed', refresh); };
  }, [user]);

  // Global inventory-sync indicator — only for roles allowed to see inventory sync.
  useEffect(() => {
    if (!user || !canSeeInventorySync) return;
    const refresh = () => WarehouseAPI.getUnicommerceStatus().then(s => setUcSyncing(!!(s && s.isRunning))).catch(() => undefined);
    refresh();
    const t = setInterval(refresh, 15000);
    window.addEventListener('uc-sync-changed', refresh);
    return () => { clearInterval(t); window.removeEventListener('uc-sync-changed', refresh); };
  }, [user, canSeeInventorySync]);

  // Avatar: single initial on a deterministic colour picked from a CURATED on-brand palette
  // (warm tones that sit with Salty orange/beige/ink) — not a raw hashed hue, which could land
  // on a clashing magenta/cyan. Each user consistently gets the same tasteful colour.
  const avatarInitial = (user?.name?.trim()?.charAt(0) || 'U').toUpperCase();
  const avatarGradient = React.useMemo(() => {
    // Curated warm/earthy brand palette — every option harmonises with the Salty theme.
    const PALETTE = [
      ['#DC5829', '#B8451E'], // Salty orange
      ['#C2410C', '#9A3412'], // burnt orange
      ['#B45309', '#92400E'], // amber/ochre
      ['#A16207', '#854D0E'], // mustard
      ['#9D5C3F', '#7C4A32'], // terracotta/clay
      ['#6B625B', '#4A433D'], // warm stone (neutral)
      ['#8A5A44', '#6E4633'], // cocoa
    ];
    const seed = (user?.name || user?.email || 'User');
    let h = 0;
    for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
    const [from, to] = PALETTE[h % PALETTE.length];
    return `linear-gradient(135deg, ${from} 0%, ${to} 100%)`;
  }, [user?.name, user?.email]);

  const handleDashboardClick = () => {
    navigate('/');
  };

  const handleSettingsClick = () => {
    navigate('/settings');
    setUserMenuAnchor(null);
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = () => {
    logout();
    setUserMenuAnchor(null);
  };

  const handleWhatsNewClick = () => {
    openReleaseHistory();
    setUserMenuAnchor(null);
  };

  const handleTakeTourClick = () => {
    // Replay path: startTour ignores salty_tourCompleted (only the auto-start
    // effect checks it), so this always restarts the tour.
    startTour();
    setUserMenuAnchor(null);
  };

  return (
    <AppBar position="static" elevation={0} sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'grey.200' }}>
      <Toolbar sx={{ minHeight: '72px !important', px: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexGrow: 1 }}>
          {/* Company Logo */}
          <Box
            component="img"
            src="/logo/salty-wordmark-orange.png"
            alt="Salty"
            sx={{ height: 22, cursor: 'pointer', display: 'block' }}
            onClick={handleDashboardClick}
          />

          {/* App Title - hidden on small screens (phone) to save space) */}
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1.25 }}>
            <Box sx={{ width: '1px', height: 18, bgcolor: 'grey.300' }} />
            <Typography
              variant="h6"
              component="div"
              sx={{
                cursor: 'pointer',
                color: 'text.primary',
                fontWeight: 600,
                fontSize: '1.1rem',
                '&:hover': {
                  color: 'primary.main',
                },
              }}
              onClick={handleDashboardClick}
            >
              WMS
            </Typography>
          </Box>
        </Box>

        {/* Action Buttons — Actionables is the only nav icon here now; Dashboard (logo),
            Inventory (sidebar + dashboard card) and Settings (user menu) are reachable
            elsewhere. The inventory-syncing status chip stays as a live indicator. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Inventory-syncing status chip stays gated on canSeeInventorySync (only roles
              allowed to see inventory sync), shown while a sync is running. */}
          {canSeeInventorySync && ucSyncing && (
            <Chip
              size="small"
              icon={<CircularProgress size={14} sx={{ color: '#DC5829' }} />}
              label="Inventory syncing…"
              sx={{
                bgcolor: 'rgba(220,88,41,0.12)',
                color: '#DC5829',
                fontWeight: 600,
                '& .MuiChip-icon': { color: '#DC5829' },
              }}
            />
          )}

          {/* Actionables icon is shown for ALL authenticated users (matches /actionables
              being ungated in App.tsx). */}
          <Tooltip title={openActionables ? `Actionables · ${openActionables} open${actionablesScope === 'all' ? ' across the team' : ' for you'}` : 'Actionables'} placement="bottom" arrow>
            <IconButton
              data-tour={TOUR.HEADER_ACTIONABLES}
              aria-label="Actionables"
              onClick={() => navigate('/actionables')}
              sx={{ color: 'text.primary', bgcolor: 'grey.100', '&:hover': { bgcolor: 'primary.light', color: 'white' } }}
            >
              <Badge badgeContent={openActionables} color="primary" max={99} invisible={!openActionables}>
                <Checklist />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* User Avatar — single initial on a deterministic, name-derived gradient so each
              person gets a consistent, distinct colour. A subtle ring + hover lift make it read
              as an interactive, polished element rather than a flat coloured circle. */}
          <Tooltip title={user?.name || 'User Menu'}>
            <IconButton
              data-tour={TOUR.HEADER_USER_MENU}
              onClick={handleUserMenuOpen}
              sx={{
                p: 0.25,
                ml: 1,
                borderRadius: '50%',
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                '&:hover': { transform: 'translateY(-1px)' },
              }}
            >
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  fontSize: '1rem',
                  fontWeight: 700,
                  lineHeight: 1,
                  letterSpacing: 0, // single letter: no tracking, so it sits optically centered
                  color: '#ffffff',
                  background: avatarGradient,
                  border: '2px solid #ffffff',
                  boxShadow: '0 0 0 1px rgba(26,20,15,0.08), 0 2px 6px rgba(26,20,15,0.16)',
                }}
              >
                {avatarInitial}
              </Avatar>
            </IconButton>
          </Tooltip>

          {/* User Menu */}
          <Menu
            anchorEl={userMenuAnchor}
            open={Boolean(userMenuAnchor)}
            onClose={handleUserMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            sx={{
              '& .MuiPaper-root': {
                minWidth: 200,
                mt: 1,
              },
            }}
          >
            <MenuItem disabled>
              <ListItemIcon>
                <Person fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={user?.name || 'User'}
                secondary={user?.email}
              />
            </MenuItem>
            {canViewSettings && (
              <MenuItem onClick={handleSettingsClick}>
                <ListItemIcon>
                  <Settings fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Settings" />
              </MenuItem>
            )}
            <MenuItem onClick={handleWhatsNewClick}>
              <ListItemIcon>
                <NewReleases fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="What's New" />
            </MenuItem>
            <MenuItem onClick={handleTakeTourClick}>
              <ListItemIcon>
                <Explore fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Take a tour" />
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;